import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useLocation } from "wouter";

export type BrandType = "aegis360" | "greenshield";

export interface BrandConfig {
  type: BrandType;
  name: string;
  logo: string;
  domain: string;
  primaryColor: string;
  secondaryColor: string;
  tagline: string;
  primaryCTA: string;
  secondaryCTA: string;
  businessType: "B2B" | "B2C";
  description: string;
}

export const BRAND_CONFIGS: Record<BrandType, BrandConfig> = {
  aegis360: {
    type: "aegis360",
    name: "Aegis360",
    logo: "🛡️", // Can be replaced with actual logo
    domain: "aegis360.co.uk",
    primaryColor: "hsl(210, 100%, 50%)", // Professional blue
    secondaryColor: "hsl(210, 100%, 95%)",
    tagline: "Increase Profit, Efficiency, and Customer Satisfaction",
    primaryCTA: "Request a Demo",
    secondaryCTA: "Start Free Trial",
    businessType: "B2B",
    description: "Professional SaaS platform for pest control businesses",
  },
  greenshield: {
    type: "greenshield",
    name: "Greenshield Pest Management",
    logo: "🛡️", // Can be replaced with actual logo
    domain: "greenshieldpm.co.uk",
    primaryColor: "hsl(142, 71%, 45%)", // Trustworthy green
    secondaryColor: "hsl(142, 71%, 95%)",
    tagline: "Fast, Effective, and Eco-Friendly Pest Solutions",
    primaryCTA: "Call Now",
    secondaryCTA: "Get a Free Quote",
    businessType: "B2C",
    description: "Local pest control services you can trust",
  },
};

interface BrandingContextType {
  currentBrand: BrandConfig;
  setBrand: (brand: BrandType) => void;
  isAegis360: boolean;
  isGreenshield: boolean;
}

const BrandingContext = createContext<BrandingContextType | undefined>(
  undefined,
);

// Module-level brand detection — safe to call before any React hook runs.
// `hostname` comes from window.location.hostname; `pathname` is either
// window.location.pathname (lazy initializer) or wouter's location (useEffect).
function detectBrand(hostname: string, pathname: string): BrandType {
  const searchParams = new URLSearchParams(window.location.search);

  // ── 1. Explicit ?brand= override (dev / Replit preview) ────────────
  const brandParam = searchParams.get("brand");
  if (brandParam === "aegis360") return "aegis360";
  if (brandParam === "greenshield") return "greenshield";

  // ── 2. Dual-brand path prefixes ─────────────────────────────────────
  if (pathname.startsWith("/platform/")) return "aegis360";
  if (pathname.startsWith("/technology/")) return "greenshield";

  // ── 3. Specific Aegis360 B2B routes ─────────────────────────────────
  const aegis360Routes = [
    "/saas",
    "/demo",
    "/features",
    // '/pricing', // REMOVED: Pricing should respect current brand context
    "/case-studies",
    "/enterprise",
    "/api-docs",
    "/integration",
    "/book-demo",
    "/customer-management", // CRM → Aegis360
    "/crm",
    "/admin", // Admin routes → Aegis360
    "/admin/",
    "/admin-command-center",
    "/backend-admin",
    "/analytics-dashboard",
    "/workflow-admin",
  ];

  if (aegis360Routes.some((route) => pathname.startsWith(route))) {
    return "aegis360";
  }

  // ── 4. Hostname-based detection (production domains) ────────────────
  if (hostname.includes("aegis360")) return "aegis360";

  // ── 5. Default ───────────────────────────────────────────────────────
  return "greenshield";
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  // Synchronous lazy initializer: first render always uses the correct brand.
  // window.location is available before React paints, so there is no
  // greenshield→aegis360 flash on app.aegis360.co.uk.
  const [currentBrand, setCurrentBrand] = useState<BrandConfig>(
    () =>
      BRAND_CONFIGS[
        detectBrand(window.location.hostname, window.location.pathname)
      ],
  );
  const [location] = useLocation();

  // Re-run on every wouter navigation (Link clicks, back/forward, SPA fallback).
  // wouter's `location` is the committed pathname — no race conditions.
  useEffect(() => {
    setCurrentBrand(
      BRAND_CONFIGS[detectBrand(window.location.hostname, location)],
    );
  }, [location]); // Re-detect on every wouter navigation

  const setBrand = (brand: BrandType) => {
    setCurrentBrand(BRAND_CONFIGS[brand]);
  };

  const value: BrandingContextType = {
    currentBrand,
    setBrand,
    isAegis360: currentBrand.type === "aegis360",
    isGreenshield: currentBrand.type === "greenshield",
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    // Return default values instead of throwing error during development
    return {
      currentBrand: BRAND_CONFIGS.greenshield,
      setBrand: () => {},
      isAegis360: false,
      isGreenshield: true,
    };
  }
  return context;
}
